/**
 * tooltipUtils.js
 * Helpers for building and positioning the calendar event tooltip.
 * No React or app state dependencies — safe at module level.
 */

export const formatTooltipTime = (dateObj) => {
  if (!dateObj) return "";
  return new Date(dateObj).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Safely resolves the dock title from a FullCalendar event.
 * Background events may not carry resourceIds, so getResources() could throw.
 */
export const getDockTitle = (event, props) => {
  if (!event) return props?.dockName;

  const resourceIds = event?._def?.resourceIds;
  const hasResources = Array.isArray(resourceIds) && resourceIds.length > 0;

  if (hasResources && typeof event.getResources === "function") {
    let resources;
    try {
      resources = event.getResources();
    } catch {
      resources = null;
    }
    if (Array.isArray(resources) && resources.length > 0) {
      return resources[0]?.title;
    }
  }

  return props?.dockName;
};

/** Builds the deduped array of text lines shown inside the tooltip. */
export const buildTooltipLines = (event) => {
  if (!event) return [];

  const start = formatTooltipTime(event.start);
  const props = event.extendedProps || {};
  const dock = getDockTitle(event, props);
  const headline = props.shipmentNumber || event.title || null;

  const rawLines = [
    headline,
    props.carrierName ? `Carrier: ${props.carrierName}` : null,
    props.orderNumber ? `Order: ${props.orderNumber}` : null,
    dock ? `Dock: ${dock}` : null,
    start ? `Start: ${start}` : null,
  ].filter(Boolean);

  // Remove duplicates (e.g. if shipmentNumber === event.title)
  return rawLines.filter((line, idx) => rawLines.indexOf(line) === idx);
};

/**
 * Calculates x/y position and width for the tooltip so it stays within the viewport.
 * Returns an object ready to spread into tooltip state.
 */
export const calcTooltipPosition = (el, lines) => {
  const rect = el.getBoundingClientRect();
  const longest = lines.reduce((max, line) => Math.max(max, line.length), 0);
  const widthCh = Math.max(18, Math.min(48, longest + 4));
  const widthPx = widthCh * 8;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY || window.pageYOffset;

  const rawX = rect.left + rect.width / 2 - widthPx / 2;
  const x = Math.min(Math.max(rawX, 8), viewportWidth - widthPx - 8);

  const estHeight = 140;
  const placeAbove = rect.top > estHeight || rect.top > viewportHeight - rect.bottom;
  const y = placeAbove
    ? rect.top + scrollY - 12 - estHeight * 0.4
    : rect.bottom + scrollY + 12;

  return { x, y, lines, widthCh };
};